import { Controller } from "@hotwired/stimulus"

// Guards an open settings card against losing unsaved input.
//
// Attached to a section form or the settings tabs' shared form. Once any field changed, leaving the page, following a
// Turbo visit, or opening another card's editor asks for confirmation first. Submitting
// or cancelling the card clears the flag, so the normal flow never prompts.
export default class extends Controller {
  static targets = ["cancel"]

  connect() {
    this.dirty = false

    // Only this form's own fields count. A field nested inside it but posting elsewhere via
    // form="…" (the report-type import inputs inside the settings form) submits its own form,
    // and its navigation must not be stopped by a prompt about this one.
    this.onInput = (event) => {
      if (event.target.form !== this.element) return
      this.dirty = true
    }
    this.onSubmit = () => {
      this.dirty = false
    }
    // A confirmed Cancel is an intentional discard — ask once here, not again on the visit.
    this.onCancelClick = (event) => {
      if (!this.dirty) return
      if (this.confirmDiscard()) {
        this.dirty = false
      } else {
        event.preventDefault()
      }
    }
    this.onBeforeUnload = (event) => {
      if (!this.dirty) return
      event.preventDefault()
      // Browsers show their own wording; a non-empty returnValue is what triggers it.
      event.returnValue = ""
    }
    this.onTurboVisit = (event) => {
      if (!this.dirty) return
      if (this.confirmDiscard()) this.dirty = false
      else event.preventDefault()
    }
    // Another card's pencil and the settings tab links are plain frame links, so intercept
    // them at the document level.
    this.onDocumentClick = (event) => {
      if (!this.dirty) return
      const link = event.target.closest("[data-settings-dirty-guard-edit-link]")
      if (!link || this.element.contains(link)) return
      if (this.confirmDiscard()) this.dirty = false
      else event.preventDefault()
    }

    this.element.addEventListener("input", this.onInput)
    this.element.addEventListener("change", this.onInput)
    this.element.addEventListener("submit", this.onSubmit)
    if (this.hasCancelTarget) this.cancelTarget.addEventListener("click", this.onCancelClick)
    window.addEventListener("beforeunload", this.onBeforeUnload)
    document.addEventListener("turbo:before-visit", this.onTurboVisit)
    document.addEventListener("click", this.onDocumentClick, true)
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput)
    this.element.removeEventListener("change", this.onInput)
    this.element.removeEventListener("submit", this.onSubmit)
    if (this.hasCancelTarget) this.cancelTarget.removeEventListener("click", this.onCancelClick)
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.onTurboVisit)
    document.removeEventListener("click", this.onDocumentClick, true)
  }

  // The prompt text is supplied by the view (sections/_form_shell) so it stays in
  // config/locales rather than in JS. Without one there is nothing to ask, so let the
  // navigation through instead of showing an empty dialog.
  confirmDiscard() {
    const message = this.element.dataset.settingsDirtyGuardMessage
    if (!message) return true

    return window.confirm(message)
  }
}
